<script setup>
//region Imports
import { ref, onMounted, reactive, watch } from 'vue'
import StepHeader from '../components/StepHeader.vue'
import config from '../config/index.js'
import helpers from "../helpers/index.js"
//endregion
//region Methods
const readData = () => JSON.parse(localStorage.getItem('tc-claim-data'))

const readIssue = () => JSON.parse(localStorage.getItem('tc-claim-issue'))

const readPhotos = () => JSON.parse(localStorage.getItem('tc-claim-photos'))

const readSelectedItems = () => JSON.parse(localStorage.getItem('tc-claim-selected-items'))

const readShippingAddress = () => JSON.parse(localStorage.getItem('tc-claim-shipping-address'))

const readCustomerInformation = () => JSON.parse(localStorage.getItem('tc-claim-customer-information'))

const clickToContinue = () => {
  if (!checkbox.value) {
    alert('Please certify that any information you submit is truthful and accurate.')
    return
  }

  submitting.value = true

  fetch(`${config.cloud.url}/api/shopify/order/shipping-protection/claims`, {
    mode: 'cors',
    method: 'POST',
    cache: 'no-cache',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reason: issue?.key,
      orderId: data?.shopifyOrderId,
      userAgent: navigator.userAgent,
      orderNumber: orderNumber?.value,
      lineItemId: +items[0]['lineItemId'],
      customerNotes: issue?.customerNotes || "",
      customerEmail: customerInformation?.email ?? data?.customerEmail,
      customerName: customerInformation?.firstName + ' ' + customerInformation?.lastName,
    }),
  }).
      then(response => response.json()).
      then(data => {
        if (!data?.success) {
          alert('An error occurred while submitting your claim. Please try again later.')
          submitting.value = false
          return data
        }

        localStorage.setItem('tc-claim-success', JSON.stringify(data))
        location.replace('#/claim/success')
      }).
      catch(error => console.error(new Error(error)))
}

const clickToBack = () => {
  location.replace('#/claim/start')
}

const clickToCancel = () => {
  if (!confirm('Are you sure?')) {
    return
  }

  location.replace('#/order')
}
//endregion
//region Variables
const data = reactive(readData() ?? {})
const issue = reactive(readIssue() ?? {})
const photos = reactive(readPhotos() ?? {})
const items = reactive(readSelectedItems() ?? [])
const shippingAddress = reactive(readShippingAddress() ?? {})
const customerInformation = reactive(readCustomerInformation() ?? {})

const checkbox = ref(false)
const submitting = ref(false)
const orderNumber = ref(data?.orderNumber)
//endregion
// region Copy methods
const copy = (value, section) => {
  helpers.copy(value)

  const label = document.querySelector(`.${section} .copy-label`)

  if (label === null) return

  label?.classList?.remove('hidden')
  setTimeout(() => {
    label?.classList?.add('hidden')
  }, 1500)
}

const copyAll = () => {
  let obj = {
    "Order Number": orderNumber?.value,
    "Customer Email": customerInformation?.email ?? data?.customerEmail,
    "Order Date": data?.orderDate,
    "Shipped To": shippingAddress,
    "Additional Info": issue?.customerNotes
  }
  helpers.copyAll(obj)

  const label = document.querySelector('#other-copy-all .copy-label')
  if (label === null) return

  label?.classList?.remove('hidden')
  setTimeout(() => {
    label?.classList?.add('hidden')
  }, 1500)
}
// endregion
//region Lifecycle
onMounted(() => {
  if (!orderNumber.value) {
    location.replace('/')
  }
})

watch(issue, () => {
  localStorage.setItem('tc-claim-issue', JSON.stringify(issue))
}, { deep: true })
//endregion
</script>

<template>

<!--
<figure class="w-full h-[305px] overflow-hidden bg-no-repeat bg-right bg-cover"
          style="background-image: url(https://cdn.shopify.com/s/files/1/0067/9532/9609/files/DSC01071.png?v=1713213218);">&nbsp;</figure>
-->

  <div v-if="submitting" class="flex flex-col items-center justify-center text-center p-4 min-h-80">
    <figure class="mx-auto">
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="49" viewBox="0 0 25 49" fill="none">
        <path
            d="M10.1231 39.5608V10.4135C10.1533 10.413 10.1817 10.4068 10.2113 10.4068C16.407 10.4068 20.5372 16.0344 21.9139 24.1737C25.8018 22.3518 25.6789 18.3835 23.4113 15.6703C20.497 12.1477 15.7999 10.1231 10.2113 10.1231C10.1812 10.1231 10.1533 10.1265 10.1231 10.1265V0H9.83937C9.83937 8.86811 5.10204 10.1231 0 10.1231V10.4068H5.26289V40.2489C5.26289 44.2183 8.01638 48.5909 14.3745 48.5909C18.5461 48.5909 21.3392 45.3118 22.4322 43.4073L22.1898 43.2861C21.0158 45.3118 18.4645 47.7 15.1442 47.7C11.0139 47.7 10.1231 44.3395 10.1231 39.5608Z"
            fill="#2E1319"/>
      </svg>
    </figure>
    <h1 class="text-tc-xl font-minionpro font-bold text-center tracking-wider mt-[20px]">Reviewing your claim</h1>
  </div>

  <div class="px-[20px] my-[30px] lg:px-[40px]" v-if="!submitting">

    <div class="mb-[30px]">
      <div class="max-w-6xl flex flex-row items-start mb-[30px] justify-start gap-[7px] w-3/4">
        <button type="button" @click="clickToBack">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path d="M8 7L2 13L8 19" stroke="black" stroke-width="1.5"/>
          </svg>
        </button>
        <h1 class="text-tc-subtitle font-bold font-minionpro tracking-wider text-tc-black" v-text="`Issue other than loss, theft or damage?`"></h1>
      </div>

      <p class="text-black font-avenir text-tc-xl font-normal tracking-wide leading-normal"
      >Tres Colori has put together an email for you with all your order information to send to info@trescolori.com. Make sure to review it and add any other relevant details before sending.</p>
    </div>

    <step-header :data="data" :items="items"/>

    <main class="lg:max-w-[420px] mx-auto">

      <div class="w-full flex items-baseline justify-between mb-[30px] relative"
           id="other-copy-all"
      >
        <h1 class="text-black font-avenir text-tc-xl font-normal tracking-wide"
        >Order Details</h1>
        <button @click="copyAll"
                class="text-black font-avenir text-tc-xl font-normal tracking-wide underline capitalize"
                v-text="'copy all'"
        ></button>
        <span class="absolute top-[2px] right-[70px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied all</span>
      </div>

      <ul class="grid grid-cols-1 gap-[20px]">

        <li v-if="data?.orderNumber" class="relative orderNumber">
          <h2 class="text-tc-muted-brown font-avenir text-tc-lg font-normal tracking-wide mb-[7px]">Order Number</h2>
          <p class="text-black font-avenir text-tc-xl font-normal tracking-wide" v-text="data?.orderNumber"></p>
          <hr class="bg-tc-off-white-two mt-[20px]">
          <button @click="copy(data?.orderNumber, 'orderNumber')"
                  class="float-right absolute top-0 right-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <g id="copy">
                <path id="Rectangle 2488" d="M4.15625 3.32222V1H15.1563V12H12.8356" stroke="#030404"/>
                <rect id="Rectangle 2487" x="0.5" y="5.65625" width="10" height="10" fill="white" stroke="#030404"/>
              </g>
            </svg>
          </button>
          <span class="absolute top-[2px] right-[30px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied</span>
        </li>

        <li v-if="data?.customerEmail" class="relative customerEmail">
          <h2 class="text-tc-muted-brown font-avenir text-tc-lg font-normal tracking-wide mb-[7px]">Email</h2>
          <p class="text-black font-avenir text-tc-xl font-normal tracking-wide" v-text="data?.customerEmail"></p>
          <hr class="bg-tc-off-white-two mt-[20px]">
          <button @click="copy(data?.customerEmail, 'customerEmail')"
                  class="float-right absolute top-0 right-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <g id="copy">
                <path id="Rectangle 2488" d="M4.15625 3.32222V1H15.1563V12H12.8356" stroke="#030404"/>
                <rect id="Rectangle 2487" x="0.5" y="5.65625" width="10" height="10" fill="white" stroke="#030404"/>
              </g>
            </svg>
          </button>
          <span class="absolute top-[2px] right-[30px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied</span>
        </li>

        <li v-if="data?.orderDate" class="relative orderDate">
          <h2 class="text-tc-muted-brown font-avenir text-tc-lg font-normal tracking-wide mb-[7px]">Order Date</h2>
          <p class="text-black font-avenir text-tc-xl font-normal tracking-wide" v-text="data?.orderDate"></p>
          <hr class="bg-tc-off-white-two mt-[20px]">
          <button @click="copy(data?.orderDate, 'orderDate')"
                  class="float-right absolute top-0 right-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <g id="copy">
                <path id="Rectangle 2488" d="M4.15625 3.32222V1H15.1563V12H12.8356" stroke="#030404"/>
                <rect id="Rectangle 2487" x="0.5" y="5.65625" width="10" height="10" fill="white" stroke="#030404"/>
              </g>
            </svg>
          </button>
          <span class="absolute top-[2px] right-[30px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied</span>
        </li>

        <li v-if="data?.shippingAddress" class="relative shippingAddress">
          <h2 class="text-tc-muted-brown font-avenir text-tc-lg font-normal tracking-wide mb-[7px]">Shipped To</h2>
          <p class="text-black font-avenir text-tc-xl font-normal tracking-wide" v-text="data?.shippingAddress"></p>
          <hr class="bg-tc-off-white-two mt-[20px]">
          <button @click="copy(data?.shippingAddress, 'shippingAddress')"
                  class="float-right absolute top-0 right-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <g id="copy">
                <path id="Rectangle 2488" d="M4.15625 3.32222V1H15.1563V12H12.8356" stroke="#030404"/>
                <rect id="Rectangle 2487" x="0.5" y="5.65625" width="10" height="10" fill="white" stroke="#030404"/>
              </g>
            </svg>
          </button>
          <span class="absolute top-[2px] right-[30px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied</span>
        </li>

        <li class="relative customerNotes">
          <h2 class="text-tc-muted-brown font-medium mb-4">Additional Information</h2>
          <textarea v-model="issue.customerNotes"
                    class="bg-tc-off-white w-full h-48 border-0 placeholder-tc-creme"
                    placeholder="What happened to your package?"
          ></textarea>
          <button @click="copy(issue?.customerNotes, 'customerNotes')"
                  class="float-right absolute top-0 right-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <g id="copy">
                <path id="Rectangle 2488" d="M4.15625 3.32222V1H15.1563V12H12.8356" stroke="#030404"/>
                <rect id="Rectangle 2487" x="0.5" y="5.65625" width="10" height="10" fill="white" stroke="#030404"/>
              </g>
            </svg>
          </button>
          <span class="absolute top-[2px] right-[30px] text-tc-lg text-tc-gray font-normal font-avenir tracking-wide hidden copy-label">copied</span>
        </li>

        <li>
          <label class="flex items-end gap-[8px]">
            <input type="checkbox"
                   class="text-black text-tc-lg font-avenir font-normal tracking-wide !w-[18px] !h-[18px] border border-solid border-black !ring-0 !outline-none !shadow-none focus:!bg-tc-maroon"
                   v-model="checkbox"
            >I certify that any information I submit is truthful and accurate.
          </label>
          <p class="text-tc-lg font-avenir font-normal tracking-wide text-tc-gray mt-[20px] leading-normal"
          >WARNING: Providing false or misleading information to Tres Colori to try to obtain compensation to which one is not entitled to is insurance fraud.</p>
        </li>

      </ul>

      <div class="my-10 flex flex-col lg:flex-row justify-between gap-5">
        <button
            type="submit"
            :disabled="submitting"
            @click="clickToContinue"
            class="text-tc-xl font-avenir font-normal tracking-wider border py-2.5 px-8 bg-tc-maroon text-white border-gray-400"
        >Submit Claim
        </button>
        <button
            type="button"
            @click="clickToCancel"
            class="text-tc-xl font-avenir font-normal tracking-wider py-2.5 px-8 border border-tc-creme text-tc-creme"
        >Cancel
        </button>
      </div>

    </main>

  </div>

</template>

<style scoped>
</style>